import { useFormikContext } from "formik";
import { FastFieldSafe } from "components/fastFieldSafe/fastFieldSafe";
import Select from "components/select/selectField";
import type { DoorLoopLeadDto } from "@/dto";
import {
  DataCy,
  isntNorthAmerica,
  JobTitlesMarketing,
  MainPropertyTypeEnum,
  OtherJobTitles,
  WhenPlanToUseSoftware,
  YesNoEnum
} from "@/dto";
import { Grid } from "@material-ui/core";
import { useBlur } from "@/pages/demo/hooks/useBlur";
import {
  JOB_TITLE_MARKETING_TRANSLATIONS,
  MAIN_PROPERTY_TYPE_TRANSLATIONS,
  OTHER_JOB_TITLE_TRANSLATIONS,
  WHEN_PLAN_TO_USE_SOFTWARE_TRANSLATIONS
} from "@/pages/demo/translationObjects";

const customPropertyTypeEnumOrder = [
  "RESIDENTIAL",
  "COMMERCIAL",
  "RESIDENTIAL_COMMERCIAL",
  "COMMUNITY_ASSOCIATIONS",
  "AFFORDABLE_HOUSING",
  "STORAGE",
  "SHORT_TERM_RENTALS",
  "OTHER"
];

export const UserDetailsStep = () => {
  const { values, setFieldValue } = useFormikContext<DoorLoopLeadDto>();
  const { makeBlurTracking } = useBlur();

  const handleSelectedJobTitleChange = (value: string) => {
    if (value !== JobTitlesMarketing.OTHER) {
      setFieldValue("jobTitle", undefined);
    }

    makeBlurTracking("user_selected_occupation", value);
  };

  return (
    <div className="mt-5 flex flex-col space-y-5">
      <Grid item xs={12}>
        <FastFieldSafe
          component={Select}
          name={"selectedJobTitle"}
          onChange={handleSelectedJobTitleChange}
          label={"What is your role?"}
          selectionEnum={JobTitlesMarketing}
          translationObject={JOB_TITLE_MARKETING_TRANSLATIONS}
          uniqueKey={"rolesSelector"}
          useEnumValue
          displayEmpty
          required
          dataCy={DataCy.marketing.demoForm.companyInfoStep.role}
        />
      </Grid>
      {values.selectedJobTitle === JobTitlesMarketing.OTHER && (
        <Grid item xs={12}>
          <FastFieldSafe
            component={Select}
            name={"jobTitle"}
            onChange={(value) => makeBlurTracking("are_you_a_tenant__attorney__or_accountant_", value)}
            label={"I am a..."}
            selectionEnum={OtherJobTitles}
            translationObject={OTHER_JOB_TITLE_TRANSLATIONS}
            uniqueKey={"otherTitlesSelector"}
            useEnumValue
            displayEmpty
            required
            dataCy={DataCy.marketing.demoForm.companyInfoStep.other}
          />
        </Grid>
      )}
      <Grid item xs={12}>
        <FastFieldSafe
          component={Select}
          name={"mainPropertyType"}
          onChange={(value) => makeBlurTracking("primary_portfolio__type", value)}
          label={"What is your main property type?"}
          selectionEnum={MainPropertyTypeEnum}
          customEnumOrder={customPropertyTypeEnumOrder}
          translationObject={MAIN_PROPERTY_TYPE_TRANSLATIONS}
          uniqueKey={"mainPropertyTypeSelector"}
          useEnumValue
          displayEmpty
          required
          dataCy={DataCy.marketing.demoForm.companyInfoStep.type}
        />
      </Grid>
      <Grid item xs={12}>
        <FastFieldSafe
          component={Select}
          name={"whenPlanToUseSoftware"}
          onChange={(value) => makeBlurTracking("customer_reported_timeline__c", value)}
          label={"When do you plan on using the software?"}
          selectionEnum={WhenPlanToUseSoftware}
          translationObject={WHEN_PLAN_TO_USE_SOFTWARE_TRANSLATIONS}
          uniqueKey={"whenPlanToUseSoftwareSelector"}
          useEnumValue
          displayEmpty
          required
          dataCy={DataCy.marketing.demoForm.companyInfoStep.WhenPlanToUseSoftware}
        />
      </Grid>
      {isntNorthAmerica(values.country || "") && (
        <Grid item xs={12}>
          <FastFieldSafe
            component={Select}
            name={"doYouManagePropertiesInTheUSOrCanada"}
            onChange={(value) => makeBlurTracking("manage_properties_us_canada", value)}
            label={"Do you manage properties in the US or Canada?"}
            selectionEnum={YesNoEnum}
            translationObject={{
              [YesNoEnum.YES]: "Yes",
              [YesNoEnum.NO]: "No"
            }}
            uniqueKey={"doYouManagePropertiesInTheUSOrCanadaSelector"}
            useEnumValue
            displayEmpty
            required
            dataCy="doYouManagePropertiesInTheUSOrCanada"
          />
        </Grid>
      )}
    </div>
  );
};
